/**
 * Captura del `beforeinstallprompt` del navegador.
 *
 * Existe por una regla dura de `docs/03` §6.1: **el evento se suprime
 * siempre**. Chrome, por defecto, muestra su propia barra de instalación
 * en el momento que su heurística decida — al abrir, a mitad del flujo de
 * agregar lugar, junto al pedido de ubicación. Los tres están
 * explícitamente prohibidos por §6.3.
 *
 * Así que se intercepta, se guarda, y el prompt nativo solo se dispara
 * desde nuestro afordance en el momento que ADR-015 autoriza.
 *
 * **En iOS este evento no llega nunca**: Safari no implementa la API. Ahí
 * `puedeInstalarNativo` es siempre `false` y la UI da instrucciones
 * manuales. No es un fallo que haya que manejar, es la plataforma.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * El evento no está en lib.dom: es de Chromium y el estándar nunca lo
 * adoptó. Se declara lo mínimo que se usa.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPrompt {
  /** ¿Hay un prompt nativo guardado y listo? En iOS, nunca. */
  puedeInstalarNativo: boolean;
  /**
   * Dispara el prompt nativo. Devuelve lo que el usuario eligió, o `null`
   * si no había prompt que disparar.
   */
  instalar: () => Promise<'accepted' | 'dismissed' | null>;
  /** El navegador confirmó la instalación. En iOS no se dispara. */
  instalada: boolean;
}

export function useInstallPrompt(): InstallPrompt {
  /*
   * El evento vive en una ref y no en estado: es un objeto del navegador
   * con métodos, no un valor que se renderice. Lo que sí es estado es si
   * existe o no, que es lo único que la UI necesita saber.
   */
  const evento = useRef<BeforeInstallPromptEvent | null>(null);
  const [disponible, setDisponible] = useState(false);
  const [instalada, setInstalada] = useState(false);

  useEffect(() => {
    const alPreguntar = (e: Event) => {
      // LA línea. Sin ella, el navegador muestra su barra cuando quiera.
      e.preventDefault();
      evento.current = e as BeforeInstallPromptEvent;
      setDisponible(true);
    };

    const alInstalar = () => {
      setInstalada(true);
      // Ya está puesta: el prompt guardado no sirve para nada.
      evento.current = null;
      setDisponible(false);
    };

    window.addEventListener('beforeinstallprompt', alPreguntar);
    window.addEventListener('appinstalled', alInstalar);

    return () => {
      window.removeEventListener('beforeinstallprompt', alPreguntar);
      window.removeEventListener('appinstalled', alInstalar);
    };
  }, []);

  const instalar = useCallback(async () => {
    const e = evento.current;
    if (!e) return null;

    /*
     * El evento se consume al usarse: el navegador lo invalida y volver a
     * llamarlo lanza. Se descarta ANTES de esperar la respuesta para que
     * un doble toque no lo dispare dos veces.
     */
    evento.current = null;
    setDisponible(false);

    try {
      await e.prompt();
      const { outcome } = await e.userChoice;
      return outcome;
    } catch {
      // El navegador puede rechazarlo (ya instalada, gesto no confiable).
      // No hay nada que el usuario pueda hacer, y romper la pantalla por
      // esto sería peor que no ofrecerlo.
      return null;
    }
  }, []);

  return { puedeInstalarNativo: disponible, instalar, instalada };
}
